import { type MouseEvent, useRef } from 'react';
import Link from 'next/link';
import { useActivePractice, useCurrentUser } from '@witmetrics/data-store';
import { formatUserName } from '@witmetrics/utils/strings';
import { useAppState } from '@/providers/AppStateProvider';
import { DIALOGS } from '@/constants/dialogs';
import { useToggle } from '@/hooks/useToggle';
import Menu, { MenuItem } from '@/components/Menu';
import {
  LocationIcon,
  LogoutIcon,
  PersonIcon,
  SupportIcon,
  SwapIcon,
} from '@/icons';
import ProfilePicture from '@/components/ProfilePicture';
import {
  ACCOUNT_SETTINGS_LINK,
  PRACTICE_SETTINGS_LINK,
  SUPPORT_LINK,
} from './constants';
import { classes } from './utils';

export type UserMenuProps = {
  onLogout: () => void;
};

export default function UserMenu({ onLogout }: UserMenuProps) {
  const menuToggleRef = useRef(null);
  const { onOpenDialog } = useAppState();
  const currentUser = useCurrentUser();
  const activePractice = useActivePractice();
  const [isMenuOpen, toggleMenu] = useToggle(false);

  const handleOpenAccounts = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onOpenDialog(DIALOGS.ACCOUNTS);
  };

  const handleSwitchAccounts = () => {
    onOpenDialog(DIALOGS.ACCOUNTS);
    toggleMenu(false);
  };

  if (!currentUser) return null;

  return (
    <div>
      <div
        ref={menuToggleRef}
        className={classes.wrapper}
        onClick={() => toggleMenu()}>
        <ProfilePicture user={currentUser} size={36} />
        <div className={classes.label}>
          <div className={classes.name}>{formatUserName(currentUser)}</div>
          {activePractice && (
            <button className={classes.account} onClick={handleOpenAccounts}>
              {activePractice.name}
            </button>
          )}
        </div>
      </div>
      <Menu
        anchorTo={menuToggleRef.current}
        open={isMenuOpen}
        onClose={() => toggleMenu()}>
        <div className={classes.header}>
          <ProfilePicture
            className={classes.headerAvatar}
            user={currentUser}
            size={36}
          />
          <div className={classes.headerInfo}>
            <div className={classes.headerName}>
              {formatUserName(currentUser)}
            </div>
            <div className={classes.headerEmail}>{currentUser.email}</div>
          </div>
        </div>
        <Link href={ACCOUNT_SETTINGS_LINK}>
          <MenuItem
            className={classes.menuItem}
            label="Manage profile"
            icon={<PersonIcon />}
          />
        </Link>
        <div className={classes.sectionLabel}>Account</div>
        {activePractice && (
          <Link
            href={`${PRACTICE_SETTINGS_LINK}?practiceId=${activePractice.id}`}>
            <MenuItem
              className={classes.menuItem}
              label="Practice settings"
              icon={<LocationIcon />}
            />
          </Link>
        )}
        <MenuItem
          className={classes.menuItem}
          label="Switch accounts"
          icon={<SwapIcon />}
          onClick={handleSwitchAccounts}
        />
        <Link href={SUPPORT_LINK} target="_blank" onClick={() => toggleMenu()}>
          <MenuItem
            className={classes.menuItem}
            label="Help Center"
            icon={<SupportIcon />}
          />
        </Link>
        <MenuItem
          destructive
          className={classes.menuItem}
          label="Sign out"
          icon={<LogoutIcon />}
          onClick={onLogout}
        />
      </Menu>
    </div>
  );
}
